import { getCurrentConfig, getApiScope } from '../config';
import { DEFAULT_HEADERS } from '../config/constants';
import { logger } from '../utils/logging/logger';

interface OccupancyApiResponse {
  success: boolean;
  message: string;
  timestamp: string;
  data: {
    records: Array<{
      EVENT_TIMESTAMP: string;
      SENSINGTYPE: string;
      LOCATIONCODE: string;
      SECTIONCODE: string;
      ASSETCODE: string;
      ASSETTYPE: string;
      OCCUPANCY: number;
      ACTUALWAITINGTIME: number;
      FLOWIN: number;
      FLOWOUT: number;
      PREDICTEDWAITINGTIME: number;
      COUNTIN: number;
      COUNTOUT: number;
      COUNTDROPOUT: number;
      CROSSINGLINECOUNTIN: number;
      CROSSINGLINECOUNTOUT: number;
      RESOURCEUSAGESTATUS: number;
      RESOURCEUSAGERATE: number;
      RESOURCEUSAGETIMEPERPERSON: number;
      STAFFOCCUPANCY: number;
      TIME_STAMP_ID: number;
    }>;
    count: number;
    latest_timestamp: string;
    statistics: {
      total_records: number;
      unique_locations: number;
      unique_assets: number;
      unique_asset_types: number;
    };
    hash: string;
    cache_info: {
      provided_hash: string | null;
      current_hash: string;
      data_changed: boolean;
    };
  };
  error: null | string;
}

interface OccupancyMap {
  [assetCode: string]: number;
}

interface FlowOutMap {
  [assetCode: string]: number;
}

interface OccupancyAndFlowData {
  occupancy: OccupancyMap;
  flowOut: FlowOutMap;
}

class OccupancyService {
  private static readonly FETCH_INTERVAL = 60 * 1000; // 60 seconds
  private static fetchTimer: NodeJS.Timeout | null = null;
  private static lastSuccessfulData: OccupancyAndFlowData | null = null;
  private static lastFetchTime = 0;
  private static isCurrentlyFetching = false;
  private static subscribers: Array<(data: OccupancyAndFlowData | null, error: string | null) => void> = [];
  private static isPeriodicFetchStarted = false; // Flag to track if periodic fetch is already started

  private getBaseURL(): string {
    const config = getCurrentConfig();
    const baseUrl = config?.apiBaseUrl || process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
    return baseUrl;
  }

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const baseUrl = this.getBaseURL();
    
    const cleanBaseUrl = baseUrl.replace(/\/$/, '');
    const cleanEndpoint = endpoint.replace(/^\//, '');
    const url = `${cleanBaseUrl}/${cleanEndpoint}`;
    
    const headers: Record<string, string> = {
      ...DEFAULT_HEADERS,
    };

    const config = getCurrentConfig();
    const authEnabled = config.auth.enableAuthentication;
    
    if (authEnabled) {
      try {
        const { msalInstance } = await import('../contexts/AuthContext');
        const accounts = msalInstance.getAllAccounts();
        
        if (accounts.length > 0) {
          const tokenResponse = await msalInstance.acquireTokenSilent({
            scopes: [getApiScope()], // Use scope from .env
            account: accounts[0]
          });
          headers['Authorization'] = `Bearer ${tokenResponse.accessToken}`;
          logger.auth('OccupancyService: Auth enabled, using token');
        } else {
          console.warn('OccupancyService: Auth enabled but no accounts available');
        }
      } catch (error) {
        console.warn('OccupancyService: Auth enabled but token acquisition failed:', error);
      }
    } else {
      logger.auth('OccupancyService: Auth disabled');
    }

    if (options?.headers) {
      Object.assign(headers, options.headers);
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  private static transformApiResponse(apiData: OccupancyApiResponse['data']): OccupancyAndFlowData {
    const occupancyMap: OccupancyMap = {};
    const flowOutMap: FlowOutMap = {};
    
    // Filter for zone and queue type assets and transform to occupancy and flow maps
    apiData.records
      .filter(record => record.ASSETTYPE === 'zone' || record.ASSETTYPE === 'queue')
      .forEach(record => {
        occupancyMap[record.ASSETCODE] = record.OCCUPANCY;
        flowOutMap[record.ASSETCODE] = record.FLOWOUT;
      });
    
    return { occupancy: occupancyMap, flowOut: flowOutMap };
  }

  private async fetchOccupancyData(): Promise<OccupancyAndFlowData | null> {
    if (OccupancyService.isCurrentlyFetching) {
      logger.api('OccupancyService: Fetch already in progress, skipping');
      return OccupancyService.lastSuccessfulData;
    }

    try {
      OccupancyService.isCurrentlyFetching = true;
      logger.api('OccupancyService: Fetching occupancy data from API...');
      
      const data: OccupancyApiResponse = await this.request<OccupancyApiResponse>('lidardatacomplete/', {
        method: 'GET'
      });
      
      if (data.success && data.data && data.data.records.length > 0) {
        const occupancyAndFlowData = OccupancyService.transformApiResponse(data.data);
        
        OccupancyService.lastSuccessfulData = occupancyAndFlowData;
        OccupancyService.lastFetchTime = Date.now();
        
        logger.api('OccupancyService: Occupancy data fetched successfully:', {
          count: Object.keys(occupancyAndFlowData.occupancy).length,
          timestamp: data.data.latest_timestamp,
          totalRecords: data.data.count
        });
        
        // Notify all subscribers
        OccupancyService.notifySubscribers(occupancyAndFlowData, null);
        
        return occupancyAndFlowData;
      } else {
        console.warn('OccupancyService: API returned empty or invalid data, keeping previous data');
        return OccupancyService.lastSuccessfulData;
      }
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('OccupancyService: Failed to fetch occupancy data:', error);
      
      // Notify subscribers of error but keep previous data
      OccupancyService.notifySubscribers(OccupancyService.lastSuccessfulData, errorMessage);
      
      return OccupancyService.lastSuccessfulData;
    } finally {
      OccupancyService.isCurrentlyFetching = false;
    }
  }

  // Get current occupancy data
  async getOccupancyData(): Promise<OccupancyMap | null> {
    // If we have recent data (less than 5 minutes old), return it
    const now = Date.now();
    if (OccupancyService.lastSuccessfulData && (now - OccupancyService.lastFetchTime) < 5 * 60 * 1000) {
      return OccupancyService.lastSuccessfulData.occupancy;
    }

    // Otherwise fetch fresh data
    const data = await this.fetchOccupancyData();
    return data ? data.occupancy : null;
  }

  // Subscribe to occupancy data updates
  static subscribe(callback: (data: OccupancyAndFlowData | null, error: string | null) => void): () => void {
    this.subscribers.push(callback);
    
    // Immediately call with current data if available
    if (this.lastSuccessfulData) {
      callback(this.lastSuccessfulData, null);
    }
    
    // Return unsubscribe function
    return () => {
      const index = this.subscribers.indexOf(callback);
      if (index > -1) {
        this.subscribers.splice(index, 1);
      }
    };
  }

  // Notify all subscribers
  private static notifySubscribers(data: OccupancyAndFlowData | null, error: string | null): void {
    this.subscribers.forEach(callback => {
      try {
        callback(data, error);
      } catch (err) {
        console.error('OccupancyService: Error in subscriber callback:', err);
      }
    });
  }

  // Start automatic periodic fetching
  static startPeriodicFetch(): void {
    // If periodic fetch is already started and timer exists, don't start another one
    if (this.isPeriodicFetchStarted && this.fetchTimer) {
      return;
    }
    
    // Clear any existing timer first (safety check)
    if (this.fetchTimer) {
      clearInterval(this.fetchTimer);
      this.fetchTimer = null;
    }
    
    logger.api('OccupancyService: Starting periodic fetch (60 second intervals)');
    
    // Mark as started
    this.isPeriodicFetchStarted = true;
    
    // Fetch immediately on start
    const service = new OccupancyService();
    service.fetchOccupancyData();
    
    // Then fetch every 60 seconds
    this.fetchTimer = setInterval(async () => {
      try {
        const service = new OccupancyService();
        await service.fetchOccupancyData();
      } catch (error) {
        console.error('OccupancyService: Error in periodic fetch:', error);
      }
    }, this.FETCH_INTERVAL);
  }

  // Stop automatic fetching
  static stopPeriodicFetch(): void {
    if (this.fetchTimer) {
      clearInterval(this.fetchTimer);
      this.fetchTimer = null;
      this.isPeriodicFetchStarted = false; // Reset the flag
      logger.api('OccupancyService: Periodic fetch stopped');
    }
  }

  // Clear all data and subscribers (for logout)
  static clearAll(): void {
    this.stopPeriodicFetch();
    this.lastSuccessfulData = null;
    this.lastFetchTime = 0;
    this.subscribers = [];
    logger.api('OccupancyService: All data and subscribers cleared');
  }

  // Get last successful data without fetching
  static getLastData(): OccupancyAndFlowData | null {
    return this.lastSuccessfulData;
  }

  // Check if we have any data
  static hasData(): boolean {
    return this.lastSuccessfulData !== null;
  }
}

// Export singleton instance
export const occupancyService = new OccupancyService();

// Export static methods for convenience
export const OccupancyServiceStatic = {
  subscribe: OccupancyService.subscribe.bind(OccupancyService),
  startPeriodicFetch: OccupancyService.startPeriodicFetch.bind(OccupancyService),
  stopPeriodicFetch: OccupancyService.stopPeriodicFetch.bind(OccupancyService),
  clearAll: OccupancyService.clearAll.bind(OccupancyService),
  getLastData: OccupancyService.getLastData.bind(OccupancyService),
  hasData: OccupancyService.hasData.bind(OccupancyService)
};
